/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 *
 * Global Angular error handler.
 *
 * Recognises the typed `AppError` subclasses produced by `errorResponseInterceptor`
 * and reacts differently per error kind instead of showing a generic toast for every
 * failure:
 *
 * | Error kind       | Action                                              |
 * | ---------------- | --------------------------------------------------- |
 * | `AuthError`      | Redirect to `/login` — no toast                     |
 * | `ValidationError`| Silently ignored — handled locally by the service   |
 * | `NetworkError`   | Warning toast via `errors.network_error` i18n key   |
 * | Other `ApiError` | Error toast with `general.error_message` i18n key   |
 * | Unknown JS error | Error toast with `general.error_message` i18n key   |
 * | Discarded msg    | Silently ignored (e.g. cancelled print requests)    |
 */

import {ErrorHandler, Injectable, inject} from '@angular/core';
import {Router} from '@angular/router';
import {ToastService} from '@siemens/ix-angular';
import {TranslateService} from '@ngx-translate/core';
import {
  AppError,
  AuthError,
  NetworkError,
  ValidationError,
} from '../models/errors/api-error';

const GENERAL_ERROR_KEY = 'general.error_message';

@Injectable({
  providedIn: 'root',
})
export class GlobalErrorHandlerService extends ErrorHandler {
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);

  private readonly errorsToDiscard = [
    'This print request was cancelled or completed',
  ];

  override handleError(error: unknown): void {
    // 401 / 403 — redirect to login, no toast
    if (error instanceof AuthError) {
      this.router.navigate(['/login']);
      return;
    }

    // 400 validation errors — always handled locally by the service/component
    if (error instanceof ValidationError) {
      return;
    }

    // Network error / offline
    if (error instanceof NetworkError) {
      this.showToast('errors.network_error', 'warning');
      return;
    }

    // Typed API errors (4xx / 5xx other than 400/401/403) and other AppError subclasses
    if (error instanceof AppError) {
      if (!this.shouldDiscard(error.message)) {
        this.showToast(GENERAL_ERROR_KEY, 'error', {message: error.message});
      }
      return;
    }

    // Unknown JS errors (type errors, null-refs, etc.)
    const message =
      error instanceof Error ? error.message : String(error ?? '');
    if (!this.shouldDiscard(message)) {
      this.showToast(GENERAL_ERROR_KEY, 'error', {message});
    }
  }

  private showToast(
    key: string,
    type: 'error' | 'warning' | 'info',
    params?: Record<string, string>,
  ): void {
    const message = this.translate.instant(key, params);
    this.toastService.show({message, type});
  }

  private shouldDiscard(message: string): boolean {
    return this.errorsToDiscard.some((err) => message?.includes(err));
  }
}
